"use client";

import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import ButtonV2 from "@/components/ui-v2/ButtonV2";
import TopContactBar from "./TopContactBar";
import { useAuthState } from "@/hooks/useAuthState";
import { isOnpremProfile } from "@/lib/config/publicRuntimeConfig";
import type {
  BrandSeoLink,
  BrandSeoNavLink,
  BrandSeoProfile,
} from "@/lib/blog/brandSeoProfile";

const MegaMenuSolutions = dynamic(() => import("./MegaMenuSolutions"), {
  ssr: false,
  loading: () => null,
});

function GoogleMark() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-slate-300 text-xs font-bold text-slate-700"
    >
      G
    </span>
  );
}

interface DropdownItem {
  href: string;
  label: string;
  description?: string;
}

interface NavLink {
  href?: string;
  label: string;
  dropdown?: DropdownItem[];
  isMegaMenu?: boolean;
}

const IKAI_DEFAULT_NAV_LINKS: BrandSeoNavLink[] = [
  { href: "/", label: "Ana Sayfa" },
  { href: "/features", label: "Özellikler" },
  { href: "/cozumler", label: "Çözümler", kind: "mega-menu" },
  {
    href: "/blog",
    label: "Kaynaklar",
    kind: "dropdown",
    children: [
      {
        href: "/blog",
        label: "Blog",
        description: "İK trendleri ve ipuçları",
      },
      {
        href: "/yazilim",
        label: "İK Yazılımları",
        description: "Yazılım rehberleri",
      },
      {
        href: "/araclar",
        label: "Ücretsiz Araçlar",
        description: "İzin hesaplayıcı vb.",
      },
    ],
  },
  { href: "/pricing", label: "Fiyatlandırma" },
];

function getIkaiNavLinks(
  onpremProfile: boolean,
  brandProfile?: BrandSeoProfile
): NavLink[] {
  return (brandProfile?.navLinks || IKAI_DEFAULT_NAV_LINKS)
    .map((link: BrandSeoNavLink): NavLink | null => {
      if (onpremProfile && link.href === "/pricing") {
        return null;
      }

      if (link.kind === "mega-menu") {
        return {
          label: link.label,
          isMegaMenu: true,
        };
      }

      if (link.kind === "dropdown") {
        return {
          label: link.label,
          dropdown: (link.children || []).map((child: BrandSeoLink) => ({
            href: child.href,
            label: child.label,
            description: child.description,
          })),
        };
      }

      if (!link.href) {
        return null;
      }

      return {
        href: link.href,
        label: link.label,
      };
    })
    .filter((item: NavLink | null): item is NavLink => item !== null);
}

type DropdownToggle = (_label: string) => void;
type DropdownStateSetter = (_value: string | null) => void;
type MenuToggle = (_open: boolean) => void;

interface NavLinksProps {
  navLinks: NavLink[];
  mounted: boolean;
  pathname: string | null;
  openDropdown: string | null;
  setOpenDropdown: DropdownToggle;
  isActive: (_href: string) => boolean;
  isDropdownActive: (_dropdown: DropdownItem[]) => boolean;
  handleLinkClick: (_href: string) => void;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function DesktopRegularNavLink({
  href,
  label,
  isActive,
  handleLinkClick,
}: {
  href: string;
  label: string;
  isActive: (_href: string) => boolean;
  handleLinkClick: (_href: string) => void;
}) {
  return (
    <Link
      href={href}
      onClick={() => handleLinkClick(href)}
      className={`text-sm font-medium transition-colors relative ${
        isActive(href)
          ? "text-indigo-600"
          : "text-gray-600 hover:text-indigo-600"
      }`}
    >
      {label}
      {isActive(href) && (
        <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
      )}
    </Link>
  );
}

function MobileRegularNavLink({
  href,
  label,
  isActive,
  handleLinkClick,
}: {
  href: string;
  label: string;
  isActive: (_href: string) => boolean;
  handleLinkClick: (_href: string) => void;
}) {
  return (
    <Link
      href={href}
      className={`text-base font-medium transition-colors py-2 px-3 rounded-lg ${
        isActive(href)
          ? "text-indigo-600 bg-indigo-50"
          : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
      }`}
      onClick={() => handleLinkClick(href)}
    >
      {label}
    </Link>
  );
}

function DesktopNavLinks({
  navLinks,
  mounted,
  pathname,
  openDropdown,
  setOpenDropdown,
  isActive,
  isDropdownActive,
  handleLinkClick,
}: NavLinksProps) {
  return (
    <>
      {navLinks.map((link: NavLink) =>
        link.isMegaMenu ? (
          <div key={link.label} className="relative">
            <ButtonV2
              variant="ghost"
              size="sm"
              onClick={() => setOpenDropdown(link.label)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                mounted && pathname?.startsWith("/cozumler")
                  ? "text-indigo-600"
                  : "text-gray-600 hover:text-indigo-600"
              }`}
            >
              {link.label}
              <Chevron open={openDropdown === link.label} />
            </ButtonV2>
            <MegaMenuSolutions
              isOpen={openDropdown === link.label}
              onClose={() => setOpenDropdown(link.label)}
            />
          </div>
        ) : link.dropdown ? (
          <div key={link.label} className="relative">
            <ButtonV2
              variant="ghost"
              size="sm"
              onClick={() => setOpenDropdown(link.label)}
              className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                isDropdownActive(link.dropdown)
                  ? "text-indigo-600"
                  : "text-gray-600 hover:text-indigo-600"
              }`}
            >
              {link.label}
              <Chevron open={openDropdown === link.label} />
            </ButtonV2>
            {openDropdown === link.label && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                {link.dropdown.map((item: DropdownItem) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => handleLinkClick(item.href)}
                    className={`block px-4 py-3 hover:bg-indigo-50 transition-colors ${
                      isActive(item.href)
                        ? "bg-indigo-50 text-indigo-600"
                        : "text-gray-700"
                    }`}
                  >
                    <div className="font-medium">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-gray-600 mt-0.5">
                        {item.description}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : link.href ? (
          <DesktopRegularNavLink
            key={link.href}
            href={link.href}
            label={link.label}
            isActive={isActive}
            handleLinkClick={handleLinkClick}
          />
        ) : null
      )}
    </>
  );
}

interface MobileNavLinksProps extends NavLinksProps {
  setMobileMenuOpen: MenuToggle;
}

function MobileNavLinks({
  navLinks,
  mounted,
  pathname,
  openDropdown,
  setOpenDropdown,
  setMobileMenuOpen,
  isActive,
  isDropdownActive,
  handleLinkClick,
}: MobileNavLinksProps) {
  return (
    <>
      {navLinks.map((link: NavLink) =>
        link.isMegaMenu ? (
          <div key={link.label}>
            <ButtonV2
              variant="ghost"
              size="sm"
              onClick={() => setOpenDropdown(link.label)}
              className={`w-full flex items-center justify-between text-base font-medium py-2 px-3 rounded-lg transition-colors ${
                mounted && pathname?.startsWith("/cozumler")
                  ? "text-indigo-600 bg-indigo-50"
                  : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
              }`}
            >
              {link.label}
              <Chevron open={openDropdown === link.label} />
            </ButtonV2>
            {openDropdown === link.label && (
              <div className="ml-4 mt-1 border-l-2 border-indigo-100 pl-3">
                <MegaMenuSolutions
                  isOpen={true}
                  onClose={() => {
                    setOpenDropdown(link.label);
                    setMobileMenuOpen(false);
                  }}
                  isMobile={true}
                />
              </div>
            )}
          </div>
        ) : link.dropdown ? (
          <div key={link.label}>
            <ButtonV2
              variant="ghost"
              size="sm"
              onClick={() => setOpenDropdown(link.label)}
              className={`w-full flex items-center justify-between text-base font-medium py-2 px-3 rounded-lg transition-colors ${
                isDropdownActive(link.dropdown)
                  ? "text-indigo-600 bg-indigo-50"
                  : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
              }`}
            >
              {link.label}
              <Chevron open={openDropdown === link.label} />
            </ButtonV2>
            {openDropdown === link.label && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-indigo-100 pl-3">
                {link.dropdown.map((item: DropdownItem) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => handleLinkClick(item.href)}
                    className={`block py-2 px-2 text-sm rounded-lg ${
                      isActive(item.href)
                        ? "text-indigo-600 bg-indigo-50"
                        : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : link.href ? (
          <MobileRegularNavLink
            key={link.href}
            href={link.href}
            label={link.label}
            isActive={isActive}
            handleLinkClick={handleLinkClick}
          />
        ) : null
      )}
    </>
  );
}

interface DesktopActionsProps {
  mounted: boolean;
  hasSession: boolean;
  accountLabel: string | null;
  accountHref: string;
  onpremProfile: boolean;
  googleLoginHref: string;
  brandProfile?: BrandSeoProfile;
}

function DesktopActions({
  mounted,
  hasSession,
  accountLabel,
  accountHref,
  onpremProfile,
  googleLoginHref,
  brandProfile,
}: DesktopActionsProps) {
  const shell = brandProfile?.publicShell;
  const loginHref = shell?.loginHref || "/login";
  const registerHref = shell?.registerHref || "/register";
  const shellAccountHref = shell?.accountHref || accountHref;
  const showLogin = shell?.showLogin !== false;
  const showRegister = shell?.showRegister !== false;
  const showGoogleLogin = !onpremProfile && shell?.showGoogleLogin !== false;

  return (
    <div className="hidden min-w-[396px] items-center justify-end space-x-4 md:flex">
      {mounted && hasSession ? (
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          {accountLabel}
        </span>
      ) : null}
      {showLogin ? (
        <Link
          href={mounted && hasSession ? shellAccountHref : loginHref}
          className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors"
        >
          {mounted && hasSession ? "Hesabıma Git" : "Giriş Yap"}
        </Link>
      ) : null}
      {showGoogleLogin && mounted && !hasSession ? (
        <a
          href={googleLoginHref}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
        >
          <GoogleMark />
          Google ile Giriş
        </a>
      ) : null}
      {showRegister ? (
        <Link
          href={registerHref}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {brandProfile?.primaryCta.label ||
            brandProfile?.ctaLabel ||
            "Ücretsiz Başla"}
        </Link>
      ) : null}
    </div>
  );
}

interface MobileActionsProps extends DesktopActionsProps {
  setMobileMenuOpen: MenuToggle;
}

function MobileActions({
  mounted,
  hasSession,
  accountLabel,
  accountHref,
  onpremProfile,
  googleLoginHref,
  setMobileMenuOpen,
  brandProfile,
}: MobileActionsProps) {
  const shell = brandProfile?.publicShell;
  const loginHref = shell?.loginHref || "/login";
  const registerHref = shell?.registerHref || "/register";
  const shellAccountHref = shell?.accountHref || accountHref;
  const showLogin = shell?.showLogin !== false;
  const showRegister = shell?.showRegister !== false;
  const showGoogleLogin = !onpremProfile && shell?.showGoogleLogin !== false;

  return (
    <div className="pt-4 border-t border-gray-200 space-y-3">
      {mounted && hasSession ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-xs font-semibold text-emerald-700">
          {accountLabel}
        </div>
      ) : null}
      {showLogin ? (
        <Link
          href={mounted && hasSession ? shellAccountHref : loginHref}
          className="block text-center py-2.5 text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-gray-50"
          onClick={() => setMobileMenuOpen(false)}
        >
          {mounted && hasSession ? "Hesabıma Git" : "Giriş Yap"}
        </Link>
      ) : null}
      {showGoogleLogin && mounted && !hasSession ? (
        <a
          href={googleLoginHref}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
          onClick={() => setMobileMenuOpen(false)}
        >
          <GoogleMark />
          Google ile Giriş
        </a>
      ) : null}
      {showRegister ? (
        <Link
          href={registerHref}
          className="block text-center py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-md"
          onClick={() => setMobileMenuOpen(false)}
        >
          {brandProfile?.primaryCta.label ||
            brandProfile?.ctaLabel ||
            "Ücretsiz Başla"}
        </Link>
      ) : null}
    </div>
  );
}

function useIkaiNavbarInteractions({
  mounted,
  pathname,
  openDropdown,
  dropdownRef,
  setOpenDropdown,
  setMobileMenuOpen,
}: {
  mounted: boolean;
  pathname: string | null;
  openDropdown: string | null;
  dropdownRef: { current: HTMLDivElement | null };
  setOpenDropdown: DropdownStateSetter;
  setMobileMenuOpen: MenuToggle;
}) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (
        dropdownRef.current &&
        target instanceof Node &&
        !dropdownRef.current.contains(target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef, setOpenDropdown]);

  const isActive = (href: string) => mounted && pathname === href;
  const isDropdownActive = (dropdown: DropdownItem[]) =>
    mounted && dropdown.some((item) => pathname?.startsWith(item.href));
  const handleLinkClick = (href: string) => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };
  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  return { isActive, isDropdownActive, handleLinkClick, toggleDropdown };
}

interface NavbarHeaderProps {
  dropdownRef: { current: HTMLDivElement | null };
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  navLinks: NavLink[];
  mounted: boolean;
  pathname: string | null;
  openDropdown: string | null;
  onToggleDropdown: DropdownToggle;
  isActive: (_href: string) => boolean;
  isDropdownActive: (_dropdown: DropdownItem[]) => boolean;
  handleLinkClick: (_href: string) => void;
  hasSession: boolean;
  accountLabel: string | null;
  accountHref: string;
  onpremProfile: boolean;
  googleLoginHref: string;
  brandProfile?: BrandSeoProfile;
}

function NavbarHeader({
  dropdownRef,
  mobileMenuOpen,
  onToggleMobileMenu,
  navLinks,
  mounted,
  pathname,
  openDropdown,
  onToggleDropdown,
  isActive,
  isDropdownActive,
  handleLinkClick,
  hasSession,
  accountLabel,
  accountHref,
  onpremProfile,
  googleLoginHref,
  brandProfile,
}: NavbarHeaderProps) {
  return (
    <div className="flex justify-between items-center h-16">
      <Link href="/" className="flex shrink-0 items-center space-x-2">
        <Image
          src={brandProfile?.publicLogoPath || "/ikai-logo.webp"}
          alt={brandProfile?.logoAlt || "IKAI HR"}
          width={150}
          height={60}
          className="h-10 w-auto max-w-[150px] sm:h-12"
          decoding="async"
          fetchPriority="high"
          unoptimized
        />
      </Link>

      <div className="hidden md:flex items-center space-x-6" ref={dropdownRef}>
        <DesktopNavLinks
          navLinks={navLinks}
          mounted={mounted}
          pathname={pathname}
          openDropdown={openDropdown}
          setOpenDropdown={onToggleDropdown}
          isActive={isActive}
          isDropdownActive={isDropdownActive}
          handleLinkClick={handleLinkClick}
        />
      </div>

      <DesktopActions
        mounted={mounted}
        hasSession={hasSession}
        accountLabel={accountLabel}
        accountHref={accountHref}
        onpremProfile={onpremProfile}
        googleLoginHref={googleLoginHref}
        brandProfile={brandProfile}
      />

      <ButtonV2
        variant="ghost"
        size="sm"
        onClick={onToggleMobileMenu}
        className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6" aria-hidden="true" />
        ) : (
          <Menu className="w-6 h-6" aria-hidden="true" />
        )}
      </ButtonV2>
    </div>
  );
}

interface NavbarMobilePanelProps {
  mobileMenuOpen: boolean;
  navLinks: NavLink[];
  mounted: boolean;
  pathname: string | null;
  openDropdown: string | null;
  onToggleDropdown: DropdownToggle;
  setMobileMenuOpen: MenuToggle;
  isActive: (_href: string) => boolean;
  isDropdownActive: (_dropdown: DropdownItem[]) => boolean;
  handleLinkClick: (_href: string) => void;
  hasSession: boolean;
  accountLabel: string | null;
  accountHref: string;
  onpremProfile: boolean;
  googleLoginHref: string;
  brandProfile?: BrandSeoProfile;
}

function NavbarMobilePanel({
  mobileMenuOpen,
  navLinks,
  mounted,
  pathname,
  openDropdown,
  onToggleDropdown,
  setMobileMenuOpen,
  isActive,
  isDropdownActive,
  handleLinkClick,
  hasSession,
  accountLabel,
  accountHref,
  onpremProfile,
  googleLoginHref,
  brandProfile,
}: NavbarMobilePanelProps) {
  if (!mobileMenuOpen) {
    return null;
  }

  return (
    <div className="md:hidden py-4 border-t border-gray-200 transition-all duration-300">
      <div className="flex flex-col space-y-2">
        <MobileNavLinks
          navLinks={navLinks}
          mounted={mounted}
          pathname={pathname}
          openDropdown={openDropdown}
          setOpenDropdown={onToggleDropdown}
          setMobileMenuOpen={setMobileMenuOpen}
          isActive={isActive}
          isDropdownActive={isDropdownActive}
          handleLinkClick={handleLinkClick}
        />
        <MobileActions
          mounted={mounted}
          hasSession={hasSession}
          accountLabel={accountLabel}
          accountHref={accountHref}
          onpremProfile={onpremProfile}
          googleLoginHref={googleLoginHref}
          setMobileMenuOpen={setMobileMenuOpen}
          brandProfile={brandProfile}
        />
      </div>
    </div>
  );
}

export default function PublicNavbar({
  brandProfile,
}: {
  brandProfile?: BrandSeoProfile;
}) {
  if (brandProfile && brandProfile.id !== "ikai") {
    return <GaiaiPublicNavbar brandProfile={brandProfile} />;
  }

  return <IkaiPublicNavbar brandProfile={brandProfile} />;
}

function GaiaiPublicNavbar({
  brandProfile,
}: {
  brandProfile: BrandSeoProfile;
}) {
  const primaryCtaHref =
    brandProfile.primaryCta.href || brandProfile.ctaHref || "/#iletisim";
  const emailHref =
    brandProfile.contact.email || brandProfile.contactEmail
      ? `mailto:${brandProfile.contact.email || brandProfile.contactEmail}`
      : brandProfile.contact.contactHref || "/#iletisim";
  const navLinks = (brandProfile.navLinks || []).filter(
    (link: BrandSeoNavLink) => link.kind !== "mega-menu"
  );

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-sm">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <a
          href={brandProfile.id === "gaiai" ? "/chat-bridge/landing" : "/"}
          className="flex shrink-0 items-center gap-2 text-lg font-bold text-slate-950"
          aria-label={`${brandProfile.organizationName} ana sayfa`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-sm font-bold text-white">
            {brandProfile.brandMarkText || "G"}
          </span>
          <span>{brandProfile.organizationName}</span>
        </a>

        <div className="flex items-center gap-3">
          <details className="relative lg:hidden">
            <summary className="flex cursor-pointer list-none items-center rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 [&::-webkit-details-marker]:hidden">
              Menü
            </summary>
            <div className="absolute right-0 top-full z-50 mt-2 min-w-44 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
              {navLinks.map((link: BrandSeoNavLink) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-teal-600"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </details>
          {navLinks.map((link: BrandSeoNavLink) => (
            <a
              key={link.href}
              href={link.href}
              className="hidden text-sm font-medium text-slate-600 transition-colors hover:text-slate-950 lg:inline"
            >
              {link.label}
            </a>
          ))}
          <a
            href={primaryCtaHref}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-500 hover:text-slate-950"
          >
            {brandProfile.primaryCta.label ||
              brandProfile.ctaLabel ||
              "İletişime Geçin"}
          </a>
          {brandProfile.contact.email || brandProfile.contactEmail ? (
            <a
              href={emailHref}
              className="hidden text-sm font-semibold text-slate-600 transition-colors hover:text-slate-950 sm:inline"
            >
              {brandProfile.contact.email || brandProfile.contactEmail}
            </a>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

function IkaiPublicNavbar({
  brandProfile,
}: {
  brandProfile?: BrandSeoProfile;
}) {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { hasSession, accountHref, accountLabel, mounted } = useAuthState();
  const googleLoginHref = "/api/v1/auth/google/start?mode=login";
  const onpremProfile = isOnpremProfile();
  const navLinks = getIkaiNavLinks(onpremProfile, brandProfile);
  const { isActive, isDropdownActive, handleLinkClick, toggleDropdown } =
    useIkaiNavbarInteractions({
      mounted,
      pathname,
      openDropdown,
      dropdownRef,
      setOpenDropdown,
      setMobileMenuOpen,
    });

  return (
    <nav className="sticky top-0 z-50 shadow-sm">
      <TopContactBar />
      <div className="bg-white border-b border-gray-200 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <NavbarHeader
            dropdownRef={dropdownRef}
            mobileMenuOpen={mobileMenuOpen}
            onToggleMobileMenu={() =>
              setMobileMenuOpen((current: boolean) => !current)
            }
            navLinks={navLinks}
            mounted={mounted}
            pathname={pathname}
            openDropdown={openDropdown}
            onToggleDropdown={toggleDropdown}
            isActive={isActive}
            isDropdownActive={isDropdownActive}
            handleLinkClick={handleLinkClick}
            hasSession={hasSession}
            accountLabel={accountLabel}
            accountHref={accountHref}
            onpremProfile={onpremProfile}
            googleLoginHref={googleLoginHref}
            brandProfile={brandProfile}
          />

          <NavbarMobilePanel
            mobileMenuOpen={mobileMenuOpen}
            navLinks={navLinks}
            mounted={mounted}
            pathname={pathname}
            openDropdown={openDropdown}
            onToggleDropdown={toggleDropdown}
            setMobileMenuOpen={setMobileMenuOpen}
            isActive={isActive}
            isDropdownActive={isDropdownActive}
            handleLinkClick={handleLinkClick}
            hasSession={hasSession}
            accountLabel={accountLabel}
            accountHref={accountHref}
            onpremProfile={onpremProfile}
            googleLoginHref={googleLoginHref}
            brandProfile={brandProfile}
          />
        </div>
      </div>
    </nav>
  );
}
